import { useInfiniteQuery } from '@tanstack/react-query';
import { axios } from '@/lib/axios/axios';
import { InfiniteQueryConfig } from '@/lib/react-query/react-query';
import {
  MESSAGING_API_BASE,
  messagingQueryKeys,
  DEFAULT_MESSAGES_LIMIT,
} from '../config';
import { MessageResponse } from '../types/backend';

export type MessagesListQuery = {
  channelId: number;
  beforeMessageId?: number;
  limit?: number;
};

const getMessages = async (
  params: MessagesListQuery
): Promise<MessageResponse[]> => {
  const { channelId, beforeMessageId, limit = DEFAULT_MESSAGES_LIMIT } = params;

  const queryParams = new URLSearchParams({
    limit: String(limit),
  });

  if (beforeMessageId) {
    queryParams.append('before-message-id', String(beforeMessageId));
  }

  return axios.get(
    `${MESSAGING_API_BASE}/channels/${channelId}/messages?${queryParams}`
  );
};

type QueryFnType = typeof getMessages;

type UseMessagesOptions = {
  channelId: number;
  limit?: number;
  config?: InfiniteQueryConfig<QueryFnType>;
};

export const useMessages = ({
  channelId,
  limit = DEFAULT_MESSAGES_LIMIT,
  config,
}: UseMessagesOptions) => {
  // Extract enabled from config (if provided) and combine with our channelId check
  const shouldEnable =
    config?.enabled !== undefined ? config.enabled : channelId > 0;

  return useInfiniteQuery({
    queryKey: [...messagingQueryKeys.messagesList(channelId, { limit })],
    queryFn: ({ pageParam }) => {
      return getMessages({
        channelId,
        beforeMessageId: pageParam,
        limit,
      });
    },
    enabled: shouldEnable,
    staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes (safety net)
    cacheTime: Infinity, // Keep in cache forever (memory management handled by React Query)
    refetchOnMount: false, // Don't refetch on mount - socket events + resetQueries handle updates
    refetchOnWindowFocus: false, // Don't refetch on focus - socket keeps us synced
    refetchOnReconnect: true, // DO refetch on reconnect - might have missed socket events
    getNextPageParam: (lastPage) => {
      // If we got a full page, there might be more
      if (lastPage.length === limit && lastPage.length > 0) {
        // Return the oldest message ID to fetch messages before it
        return lastPage[lastPage.length - 1].id;
      }

      return undefined; // No more pages
    },
    // Reverse each page to get chronological order within each page
    // Then reverse the pages array so pages are ordered oldest-page-first
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => [...page].reverse()).reverse(),
    }),
  });
};
